import { BusinessInfo } from '../../../legacy/types';
import { getDefaultTimezone } from '../timezone/timezone';
import { EnrichedService } from '../../types/types';
import {
  getPaymentDescription,
  getDiscountedPaymentsCount,
  getEffectiveDiscountInfo,
  getFormattedCurrency,
  getRemainingPayments,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { Day } from '@wix/ambassador-calendar-v3-event/types';
import {
  RateType,
  StaffMember,
} from '@wix/ambassador-bookings-services-v2-service/types';

export const getOfferedDays = (service: EnrichedService, t: Function) => {
  const daysOrder =
    t('first-day-of-week') === t('week-day.sun')
      ? [
          Day.SUNDAY,
          Day.MONDAY,
          Day.TUESDAY,
          Day.WEDNESDAY,
          Day.THURSDAY,
          Day.FRIDAY,
          Day.SATURDAY,
        ]
      : [
          Day.MONDAY,
          Day.TUESDAY,
          Day.WEDNESDAY,
          Day.THURSDAY,
          Day.FRIDAY,
          Day.SATURDAY,
          Day.SUNDAY,
        ];

  const { offeredDays } = service;

  if (!offeredDays) {
    return t('offered-days.loading');
  }

  const weekDayKeyPrefix: string =
    offeredDays.length <= 2 ? 'long-week-day.' : 'short-week-day.';
  return daysOrder
    .filter((day) => offeredDays.indexOf(day) > -1)
    .map((day) => t(weekDayKeyPrefix + day.toLowerCase().substring(0, 3)))
    .join(', ');
};

export const getFormattedStartDate = ({
  service,
  language,
  t,
  businessInfo,
}: {
  service: EnrichedService;
  language: string;
  t: Function;
  businessInfo: BusinessInfo;
}) => {
  if (service.schedule?.firstSessionStart) {
    try {
      const startDate = new Date(service.schedule?.firstSessionStart);

      const isDateInCurrentYear = (date: Date) => {
        const now = new Date();
        return now.getFullYear() === date.getFullYear();
      };

      const isDateInThePast = (date: Date) => {
        const now = new Date();
        return date.valueOf() < now.valueOf();
      };

      const timeZone = getDefaultTimezone(businessInfo);
      const dateFormat: Intl.DateTimeFormatOptions = isDateInCurrentYear(
        startDate,
      )
        ? {
            month: 'short',
            day: 'numeric',
            timeZone,
          }
        : {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone,
          };

      const formattedStartDate = new Intl.DateTimeFormat(
        language,
        dateFormat,
      ).format(startDate);

      return isDateInThePast(startDate)
        ? `${t(
            'service.schedule.course.start.date.passed',
          )} ${formattedStartDate}`
        : `${t(
            'service.schedule.course.start.date.future',
          )} ${formattedStartDate}`;
    } catch (e) {
      return '';
    }
  }
  return '';
};

const getSubscriptionDiscountTooltip = (
  service: EnrichedService,
  t: Function,
): string => {
  const discountedCycleCount =
    service.payment?.discountInfo?.discountedCycleCount;
  const remainingPayments = service.payment?.subscription
    ? getRemainingPayments(service.payment.subscription)
    : undefined;

  const discountedPaymentsCount = getDiscountedPaymentsCount(
    discountedCycleCount,
    remainingPayments,
  );

  return discountedPaymentsCount === undefined
    ? t('service-list.widget.discount.subscription.all-payments')
    : t('service-list.widget.discount.subscription.first-payments', {
        count: discountedPaymentsCount,
      });
};

export const getPrice = ({
  service,
  locale,
  t,
  isMonthlyPricingDiscountsEnabled = false,
}: {
  service: EnrichedService;
  language: string;
  locale?: string;
  t: Function;
  isMonthlyPricingDiscountsEnabled?: boolean;
}): {
  displayedPrice: string;
  srOnlyPrice: string;
  priceAfterDiscount?: string;
  discountName?: string;
  discountTooltip?: string;
} => {
  const getPaymentDescriptionArgs: Parameters<typeof getPaymentDescription>[0] =
    {
      payment: service.payment!,
      regionalSettingsLocale: locale!,
      paymentFormatters: {
        variedPrice: ({ minPrice }) =>
          t('service.payment.from-price', { price: minPrice }),
        subscriptionPrice: ({ pricePerCycle }) =>
          t('service.payment.subscription.monthly-price', {
            price: pricePerCycle,
          }),
      },
    };

  const isSubscription = service.payment?.rateType === RateType.SUBSCRIPTION;
  const discountInfo = getEffectiveDiscountInfo({
    payment: service.payment,
    isMonthlyPricingDiscountsEnabled,
  });
  const priceAfterDiscount = discountInfo?.priceAfterDiscount;
  const discountValue = priceAfterDiscount?.value;
  const discountCurrency = priceAfterDiscount?.currency;

  const priceAfterDiscountFormatted =
    discountValue && discountCurrency
      ? getFormattedCurrency({
          price: discountValue,
          locale: locale!,
          currency: discountCurrency,
        })
      : undefined;

  const isSubscriptionDiscount =
    isSubscription && !!priceAfterDiscountFormatted;

  const srOnlyPrice = getPaymentDescription({
    ...getPaymentDescriptionArgs,
    currencyDisplay: 'name',
  });

  if (isSubscriptionDiscount) {
    return {
      displayedPrice: getPaymentDescription({
        ...getPaymentDescriptionArgs,
        paymentFormatters: {
          ...getPaymentDescriptionArgs.paymentFormatters,
          subscriptionPrice: ({ pricePerCycle }) => pricePerCycle,
        },
      }),
      srOnlyPrice,
      priceAfterDiscount: t('service.payment.subscription.monthly-price', {
        price: priceAfterDiscountFormatted,
      }),
      discountName: discountInfo?.discountName,
      discountTooltip: getSubscriptionDiscountTooltip(service, t),
    };
  }

  return {
    displayedPrice: getPaymentDescription(getPaymentDescriptionArgs),
    srOnlyPrice,
    priceAfterDiscount: priceAfterDiscountFormatted,
    discountName: discountInfo?.discountName,
  };
};

export const getStaffMembersDisplayText = ({
  staffMembers,
  showAllStaffMembersNames,
  multipleStaffMembersText,
}: {
  staffMembers?: StaffMember[];
  showAllStaffMembersNames: boolean;
  multipleStaffMembersText: string;
}): string | undefined => {
  const names = (staffMembers ?? [])
    .map((staffMember) => staffMember.name)
    .filter((name): name is string => !!name);

  if (names.length === 0) return undefined;
  if (names.length === 1) return names[0];
  return showAllStaffMembersNames ? names.join(', ') : multipleStaffMembersText;
};
